// Disable max-lines rule for this file,
// until prebid and a9 slot removal is implemented in each respective plugin
/* eslint-disable max-lines */
import type {
  SlotConfig,
  BaseSlotName,
  SlotName,
  SlotType,
  RefreshConfigRemoveSizes,
} from "../config/platforms/types";
import type { GptWrapper } from "./GptWrapper";
import { isElementInViewport } from "../helpers/isElementInViewport";
import {
  Comparator,
  type SizeTuple,
  deleteSlotSizes,
  DEFAULT_REMOVE_SIZES_CONFIG,
} from "../helpers/slotSizes";
import { getBaseSlotName } from "../helpers/repeatableSlotNames";
import { isElementNearViewport } from "../helpers/isElementNearViewport";
import {
  DEFAULT_EMPTY_RETRY_COUNT,
  DEFAULT_NODE_NEAR_VIEWPORT_MARGIN,
} from "./consts";

export enum AdNodeRenderStatus {
  NOT_RENDERED = "not-rendered",
  RENDERING = "rendering",
  RENDERED = "rendered",
}

/**
 * AdNode represents a single ad slot on the page.
 * It's not called AdSlot to avoid confusion with the GPT slot.
 */
export class AdNode {
  private renderStatus = AdNodeRenderStatus.NOT_RENDERED;
  private renderCount = 0;
  private currentSlotConfig: SlotConfig;
  private readonly baseSlotName: BaseSlotName;
  /**
   * Counts the number of times the slot has been empty in a row. After a configured number
   * of no-ads, stop refreshing the slot.
   */
  private emptyRetryCount = 0;

  /**
   * If the slot is blocked, it will not be requested
   *
   * @see isBlocked()
   * @see block()
   * @see unblock()
   * @see AdNodeManager#requestFirstCallSlotsAndObserveIntersection
   * @see FloorAdhesionPlugin#api#block
   */
  private blocked = false;
  private exactSizesToRemoveRemoved = false;

  constructor(
    private slotName: SlotName,
    private readonly slotConfig: SlotConfig,
    private readonly gptWrapper: GptWrapper,
  ) {
    // deep clone to avoid mutating the original config
    this.currentSlotConfig = JSON.parse(JSON.stringify(slotConfig));
    this.baseSlotName = getBaseSlotName(slotName);

    this.hookAdNodeToGptEvents();
  }

  /**
   * Hook the ad node to gpt events. This will listen to different events and
   * trigger the appropriate actions.
   *
   * Should only be called once when the ad node is created.
   * The `this.gptWrapper` is already initialized at this point.
   */
  private hookAdNodeToGptEvents(): void {
    // if the slot can be refreshed and the refresh has limited sizes, we need to listen to the
    // `impressionViewable` event to limit the sizes for future refreshes once the slot has been rendered
    // and the creative has been fully loaded. The `slotRenderEnded` event is too early for this, since we don't have the creative's dimensions yet.
    this.enableLimitingSizesIfConfigured();
    this.listenToSlotRenderEnded();
  }

  /**
   * Enable limiting sizes for the slot. This will listen to the `impressionViewable` event and
   * remove all sizes that are different than the size of the slot that was just rendered.
   */
  private enableLimitingSizesIfConfigured(): void {
    const removeSizes = this.getRefreshConfigRemoveSizes();

    // if the slot is not refreshable or the `removeSizes` is not configured, we don't need to
    if (!removeSizes?.strategy) {
      return;
    }

    // hook the impressionViewable event to limit the sizes for future refreshes
    const unsubscribe = this.gptWrapper.addSlotEventListener(
      this.slotName,
      "impressionViewable",
      () => {
        // remove all sizes that are not equal to the slot size
        this.removeSizesFromSlotElement();

        // if the strategy is "once", we can unsubscribe from the event
        if (removeSizes.strategy === "once") {
          unsubscribe();
        }
      },
    );
  }

  /**
   * Listen for the `slotRenderEnded` event on the slot.
   *
   * - Checks if the slot `isEmpty` or not for capping refreshes when the slot
   * is empty a certain number of times. If the slot isEmpty, then
   * `emptyRetryCount` is incremented, otherwise it is reset to `0`.
   */
  private listenToSlotRenderEnded(): void {
    this.gptWrapper.addSlotEventListener(
      this.slotName,
      "slotRenderEnded",
      (event: googletag.events.SlotRenderEndedEvent) => {
        this.renderStatus = AdNodeRenderStatus.RENDERED;
        if (event.isEmpty) {
          this.emptyRetryCount += 1;
        } else {
          this.emptyRetryCount = 0;
        }

        this.handleExactSizesToRemoveAfterRender();
      },
    );
  }

  /**
   * Called when the ad slot is destroyed. This will remove all gpt listeners and destroy the slot
   * in googletag.
   */
  destroy() {
    this.gptWrapper.destroySlots([this]);
  }

  getSlotName(): SlotName {
    return this.slotName;
  }

  /**
   * This function is called in the special case where we have outOfPageSlots defined
   * with a specific googletag OutOfPageFormat.
   *
   * @see {@link OutOfPageSlotConfig.format}
   * @see {@link googletag.enums.OutOfPageFormat}
   * */
  setOutOfPageSlotName(slotName: SlotName): void {
    this.slotName = slotName;
  }

  /**
   * Get the BaseSlotName for this slot. This is needed when the slot is a repeatable slot and we
   * need to lookup configurations which are keyed by the BaseSlotName and not the repeatable name
   * with the `_<number>` suffix.
   *
   * @returns The BaseSlotName for this slot without any index values.
   */
  getBaseSlotName(): BaseSlotName {
    return this.baseSlotName;
  }

  getSlotType(): SlotType {
    // default to "display" if type is not set because it is more common
    return this.currentSlotConfig.type ?? "display";
  }

  /**
   * Get the current slot configuration for this ad node.
   *
   * @returns The current slot configuration for this ad node.
   */
  getSlotConfig(): SlotConfig {
    return this.currentSlotConfig;
  }

  /**
   * Set the slot configuration for this ad node. This will update the
   * configuration, leaving the original slotConfig from the platform
   * configuration unchanged. The changes are not propagated anywhere, but all
   * code following this set that requests the slot configuration will get the
   * updated configuration.
   *
   * @param slotConfig The new slot configuration to set for this ad node.
   */
  setSlotConfig(slotConfig: SlotConfig): void {
    this.currentSlotConfig = slotConfig;
  }

  /**
   * Retrieves the bidder configuration for the given source from the SlotConfig.bidders[source].
   *
   * For example, to get the Prebid AdUnit configurations for this slot, call:
   *
   * ```ts
   * const prebidAdUnitConfig = adNode.getBidderConfig('prebid');
   * ```
   *
   * @param source The source of the bidder config, either "prebid" or "a9"
   * @returns The bidder configuration for the given source, or undefined if not set.
   */
  getBidderConfig<S extends BidderSource>(
    source: S,
  ): SlotBiddersConfig[S] | undefined {
    return this.currentSlotConfig.bidders?.[source];
  }

  /**
   * Sets the configuration for a given bidder source in the current slot configuration.
   *
   * @param source The source of the bidder config, either "prebid" or "a9"
   * @param config The configuration to set for the given bidder source. This will be in the format
   * of the specific bidder's configuration.
   */
  setBidderConfig<S extends BidderSource>(
    source: S,
    config: SlotBiddersConfig[S],
  ) {
    this.currentSlotConfig.bidders ??= {};
    this.currentSlotConfig.bidders[source] = config;
  }

  getElement(): HTMLElement | null {
    return document.getElementById(this.slotName);
  }

  /**
   * Get the ad unit path for the current slot. If the slot has not been defined yet in gpt then
   * this will return null.
   *
   * @returns The ad unit path for the slot.
   */
  getAdUnitPath(): string | null {
    return this.gptWrapper.getAdUnitPath(this.slotName);
  }

  /**
   * Sets the provided key/value on the slot targeting in googletag. If the slot has not been
   * defined yet then this will throw an exception.
   *
   * @param key Key to use for setting the slot targeting
   * @param value The value to set for the key on the slot targeting
   * @throws Error if the slot has not been defined in googletag
   */
  setTargeting(key: string, value: string | string[]) {
    this.gptWrapper.setSlotTargeting(this.slotName, key, value);
  }

  /**
   * Retrieve the slot targeting value for the provided key. If this slot has not been defined in
   * gpt yet, then this will return an empty array.
   * @retruns The value for the provided key, or an empty array if the keyvalue doesn't exist or the
   * slot hasn't been defined in googletag.
   */
  getTargeting(key: string): string[] {
    try {
      return this.gptWrapper.getSlotTargeting(this.slotName, key);
    } catch {
      // If the function throws, the slot hasn't been defined yet, so just return empty array.
      return [];
    }
  }

  /**
   * Gets all targeting for the slot and returns an object with the key/value pairs. Every value
   * will be an array with zero or more values.
   *
   * @returns All targeting for the slot. If the slot has not been defined yet, this will return an
   * empty object.
   */
  getAllTargeting(): Record<string, string[]> {
    try {
      return this.gptWrapper.getAllSlotTargeting(this.slotName);
    } catch {
      return {};
    }
  }

  /**
   * Returns the current render status of the ad node. This can be used to determine if the ad
   * is in the process of rendering, has been rendered, or has not been rendered yet.
   */
  getRenderStatus(): AdNodeRenderStatus {
    return this.renderStatus;
  }

  /**
   * Validates if the ad node is eligible to render. This is for first call and refreshes,
   *
   * This will check the following conditions, returning false if any of these
   * conditions to not render are met (in order):
   * - The ad node blocked using the {@link AdNode.block} and
   *   {@link AdNode.unblock} methods?
   * - The render status is `RENDERING` (the ad is currently being rendered)
   * - If this is a refresh (hasEverBeenRequested is true), check the following:
   *   - Refresh is not enabled for this slot, or retry count has been exceeded
   *
   * @returns true if the ad node is eligible to render, false otherwise.
   */
  isEligibleToRender(): boolean {
    if (this.isBlocked()) {
      return false;
    }

    if (this.getRenderStatus() === AdNodeRenderStatus.RENDERING) {
      return false;
    }

    if (this.hasEverBeenRequested()) {
      // This is a refresh
      if (!this.isRefreshable()) {
        return false;
      }
    }

    return true;
  }

  /**
   * @returns true if the element is in the viewport, false if the element is not in the viewport
   */
  isInViewport() {
    const element = this.getElement();

    if (!element) {
      return false;
    }

    return isElementInViewport(element);
  }

  /**
   * Returns true if the element is in the viewport or within the margin above or below the
   * viewport. The margin is set to 200px, as defined by {@link DEFAULT_NODE_NEAR_VIEWPORT_MARGIN},
   * however, in the future, this will be a configurable value and may differ between initial
   * ad render and refresh.
   *
   * @returns true if the element is in the viewport or within the margin above or below the
   * viewport.
   */
  isNearViewport() {
    const element = this.getElement();
    return isElementNearViewport(element, DEFAULT_NODE_NEAR_VIEWPORT_MARGIN);
  }

  /**
   * Called to track that the process of ad refresh has started
   */
  incrementRenderCount() {
    this.renderCount += 1;
    this.setTargeting("rv", this.renderCount.toString());
  }

  getRenderCount(): number {
    return this.renderCount;
  }

  /**
   * Returns if the slot refreshing is enabled or not for the slot. This checks
   * the configuraton to see if configuration has refresh enabled. If it is
   * enabled then it will also check that GAM has not returned an empty slot
   * more than the allowed number of times, as currently set in `./consts.ts`
   * `DEFAULT_EMPTY_RETRY_COUNT`. This ensures we do not keep refreshing a slot
   * that is not delivering ads and dropping viewability.
   *
   * @todo The retry count should be configurable in the platform configuration,
   * via `slotConfig.refresh?.emptyRetryCount` and set to an integer value. -1
   * would be no limit, 0 means never refresh once empty, and any positive
   * integer would be the number of times to retry before stopping the refresh.
   *
   * @returns If the slot refreshing is enabled for the slot.
   */
  isRefreshable(): boolean {
    return (
      !!this.currentSlotConfig.refresh?.enabled &&
      this.emptyRetryCount < DEFAULT_EMPTY_RETRY_COUNT
    );
  }

  isBlocked(): boolean {
    return this.blocked;
  }

  block(): void {
    this.blocked = true;
  }

  unblock(): void {
    this.blocked = false;
  }

  /**
   * Determines if ads for this slot have ever been requested
   *
   * true: the first call to gptWrapper.requestSlots has been made (but may still be pending)
   * false: this ad has never been requested
   */
  hasEverBeenRequested(): boolean {
    return this.renderStatus !== AdNodeRenderStatus.NOT_RENDERED;
  }

  /**
   * Starts rendering the ad node but setting the render status to RENDERING,
   * incrementing the render count, and setting the hasEverBeenRequested to
   * true.
   */
  startRendering() {
    this.renderStatus = AdNodeRenderStatus.RENDERING;
    this.incrementRenderCount();
  }

  getSizes(): SlotConfig["responsiveSizes"] {
    return this.currentSlotConfig.responsiveSizes;
  }

  setSizes(sizes: SlotConfig["responsiveSizes"]) {
    this.currentSlotConfig.responsiveSizes = sizes;

    // update the slot mapping if the slot is already defined
    try {
      this.gptWrapper.updateSizeMapping(
        this.slotName,
        this.currentSlotConfig.responsiveSizes,
      );
    } catch {
      // slot may not be defined yet if we're calling it in one of the early hooks
      // then just update the slot config and move on
    }
  }

  /**
   * Removes sizes from the configuration `sizes` and `responsiveSizes` based on the provided size and
   * comparator. The size provided is compared against every size in the configuration using the provided
   * comparator to determine if the size should be removed or not from the configuration.
   *
   * If the GPT slot is already defined it updates its mapping.
   *
   * The sizes are compared first by height, then by width against the provided size. If the height or width
   * of the size param is `0`, then that acts as a wildcard and that height or width will not be compared.
   * @see {@link deleteSlotSizes} on detailed description of how this process works.
   *
   * @example
   * For example, to remove all sizes not equal to `[728,90]`, use
   * ```ts
   * /// Given { sizes: [[970,250], [728,90], [300,250]], responsiveSizes: [[0,0], [970,250], [728,90], [300, 250]]}
   * adNode.removeSizes([728,90], Comparator.EXACT)
   * const { sizes, responsiveSizes } = adNode.getSlotConfig()
   * /// sizes  = [[970,250], [300,250]] and responsiveSizes = [[0,0], [[970,250], [300,250]]]
   * ```
   *
   * @example
   * To remove all sizes larger than the provided height, use
   * ```ts
   * /// Given { sizes: [[970,250], [728,90], [300,250]], responsiveSizes: [[0,0], [970,250], [728,90], [300, 250]]}
   * adNode.removeSizes([0,90], Comparator.GREATER_THAN)
   * const { sizes, responsiveSizes } = adNode.getSlotConfig()
   * /// sizes  = [[728,90]] and responsiveSizes = [[0,0], [[728, 90]]]
   * ```
   *
   * @param size the size to remove from the slotConfig
   * @param comparator the comparator to use; by default it's EXACT
   */
  removeSizes(size: SizeTuple, comparator = Comparator.EXACT) {
    const { sizes, responsiveSizes } = this.currentSlotConfig;

    if (!Array.isArray(sizes)) {
      return;
    }

    const updatesResponsiveSizes: googletag.SizeMappingArray =
      responsiveSizes.map((responsiveSize: googletag.SizeMapping) => {
        const [breakpointSize, sizesArray] = responsiveSize;

        return [breakpointSize, deleteSlotSizes(sizesArray, size, comparator)];
      });

    this.setSizes(updatesResponsiveSizes);

    // TODO: Cleanup this code once we remove current `sizes` from the config.
    this.currentSlotConfig.sizes = deleteSlotSizes(sizes, size, comparator);
    this.currentSlotConfig.responsiveSizes = updatesResponsiveSizes;

    this.updateBidderConfigs(size, comparator);
  }

  /**
   * Helper to get proper configuration for removing sizes after impression.
   * There are several cases we want to support:
   * - If the slot is not refreshable, we will return undefined (removing sizes is disabled)
   * - If the `removeSizes` is set to `false`, we will return undefined (removing sizes is disabled)
   * - If the `removeSizes` is set to `undefined` (or not set), we will return undefined (removing sizes is disabled)
   * - If the `removeSizes` is set to `true`, we will return the default configuration
   * - If the `removeSizes` is set to an object, we will return that object as the configuration
   *
   * @returns The configuration for removing sizes after the GPT `impressionViewable` event fired, or undefined if the slot is not refreshable
   * or the `removeSizes` is disabled.
   */
  private getRefreshConfigRemoveSizes(): RefreshConfigRemoveSizes | undefined {
    const { removeSizes } = this.currentSlotConfig.refresh ?? {};

    // if the slot is not refreshable or removeSizes is undefined/false, we don't need to return anything
    if (!this.isRefreshable() || !removeSizes) {
      return undefined;
    }

    // if the config has `removeSizes` that is set to `true`,
    // we return the default configuration for removeSizes.
    if (removeSizes === true) {
      return DEFAULT_REMOVE_SIZES_CONFIG;
    }

    // otherwise the `removeSizes` is a configured object that we can return
    return removeSizes as RefreshConfigRemoveSizes;
  }

  /**
   * Method to remove all sizes from the slot that are not equal to the slot element.
   * This really shouldn't be called directly, but rather through the `enableLimitingSizesIfConfigured`
   * method which will hook the `impressionViewable` event to call this method.
   * But this is here for convenience and to allow manual calls if needed (e.g. in tests).
   */
  private removeSizesFromSlotElement() {
    const removeSizes = this.getRefreshConfigRemoveSizes();

    // if the slot is not refreshable or the `removeSizes` is not configured, we don't need to
    if (!removeSizes) {
      return;
    }

    /**
     * You'd think that `event.slot.getSizes()` would return the sizes of the slot that was just
     * rendered, but it doesn't. It returns the sizes of the slot that was defined in gpt.
     * So we need to use the HTML element to get the size of the slot that was just rendered.
     */
    const iframe = this.getElement()?.querySelector("iframe");
    if (!iframe) {
      return;
    }
    const iframeRect = iframe.getBoundingClientRect();

    /**
     * The size of the slot that was just rendered. This will be used to limit the sizes
     * - do not worry if the slot size isn't a valid size, the removeSizes function will take care of it
     * - also replace any non-match dimensions with `0` to use wildcard matching
     *
     * Note:
     * - The checking for `match` value can look tricky at first. If the `match` value is `height`, it means
     * that we want to match the height of the slot, so we set the width to `0` to use wildcard matching.
     * - If the `match` value is `width`, it means that we want to match the width of the slot, so we set the
     * height to `0` to use wildcard matching.
     * - If the `match` value is `both`, it means that we want to match both dimensions, so we use the actual
     * dimensions of the slot.
     */
    const slotSize: [number, number] = [
      removeSizes.match === "height" ? 0 : iframeRect.width,
      removeSizes.match === "width" ? 0 : iframeRect.height,
    ];

    // remove all sizes that are not equal to the slot size
    this.removeSizes(slotSize, removeSizes.with);
  }

  /**
   * Handles the removal of provided sizes from the current slot configuration.
   * Used for removing direct custom sizes from the slot configuration
   *
   */
  private handleExactSizesToRemoveAfterRender() {
    const sizesToRemove = this.currentSlotConfig.refresh?.exactSizesToRemove;

    if (sizesToRemove && !this.exactSizesToRemoveRemoved) {
      sizesToRemove.forEach((tuple) => {
        this.removeSizes(tuple);
      });

      this.exactSizesToRemoveRemoved = true;
    }
  }

  /**
   * Updates the bidder configurations for the current ad node by removing the provided size
   * from the sizes array in both A9 and Prebid configurations.
   *
   * This is a temporary solution until the A9 and Prebid plugins implement their own size removal logic.
   *
   * @param size The size to remove from the bidder configurations.
   * @param comparator The comparator to use for removing the size.
   */
  private updateBidderConfigs(size: SizeTuple, comparator: Comparator) {
    // TODO: This editing shouldn't be done here.
    //  Ideally, it should be done in the A9 and Prebid plugins.
    //  For now, this solution works, but increases the complexity of the AdNode class.
    this.updateA9ConfigSize(size, comparator);
    this.updatePrebidConfigSize(size, comparator);
  }

  /**
   * Temporary function to update the A9 bidder configuration.
   *
   * Updates the A9 bidder configuration by removing the provided size from the sizes array.
   * If the A9 config does not exist or does not have sizes, it will not do anything.
   *
   * @param size The size to remove from the A9 config.
   * @param comparator The comparator to use for removing the size.
   */
  private updateA9ConfigSize(size: SizeTuple, comparator: Comparator) {
    // TODO: Same as in the calling function, this should be done in the A9 plugin.
    const a9Config = this.getBidderConfig("a9");
    if (a9Config?.sizes) {
      a9Config.sizes = deleteSlotSizes(a9Config.sizes, size, comparator);
      this.setBidderConfig("a9", a9Config);
    }
  }

  /**
   * Temporary function to update the Prebid bidder configuration.
   *
   * Updates the Prebid bidder configuration by removing the provided size from the sizes array.
   * If the Prebid config does not exist or does not have sizes, it will not do anything.
   *
   * @param size The size to remove from the Prebid config.
   * @param comparator The comparator to use for removing the size.
   */
  private updatePrebidConfigSize(size: SizeTuple, comparator: Comparator) {
    // TODO: Same as in the calling function, this should be done in the Prebid plugin.
    const prebidConfig = this.getBidderConfig("prebid");
    if (prebidConfig?.mediaTypes?.banner?.sizes) {
      prebidConfig.mediaTypes.banner.sizes = deleteSlotSizes(
        prebidConfig.mediaTypes.banner.sizes as googletag.GeneralSize,
        size,
        comparator,
      ) as PrebidSize;
      this.setBidderConfig("prebid", prebidConfig);
    }
  }
}
